/**
 * Générateur — Automatisme : résoudre des équations du type ax = c et
 * x + b = c, où a, b et c sont des nombres.
 * Attendu ID : 1107 | Niveau : 4ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 10, ligne 707 :
 * « Résoudre des équations du type ax = c et x + b = c, où a, b et c sont
 * des nombres. »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * Automatisation de gen_943 (objectif 5e l. 619) AVEC LA LETTRE : c'est ce
 * qui la distingue de 1046 (addition à trou) et 1095 (5 × … = 3), tous deux
 * sans lettre (arbitrage 1063). Une étape, la solution écrite. Formes : ax =
 * c et x + b = c (b + x = c admis) ; x − b = c et b − x = c EXCLUS
 * (l'intitulé nomme la forme ; MathsMentales 4NE7 « a − x = b » rejetée).
 * Frontières : ax + b = c en deux étapes (objectif l. 717, gen_163) ; mettre
 * en équation (941) ; 1095/1049 ; 1046 ; 1099.
 * MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 4NE6 (x + a =
 * b, entiers et décimaux) et 4NE8 (ax = b) retenues ; relatifs et
 * « a décimal 0,1..1 » rejetés (quotient de relatifs = objectif l. 642,
 * division par un décimal = objectif 5e l. 498).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes KaTeX
 *     « équation, x = … » : x + b = c (entiers) ; ax = c à solution entière ;
 *     ax = c à solution en fraction (deux cases, gabarit 622/1049, c non
 *     multiple de a, irréductible) ; b + x = c à une décimale ou x + b = c.
 *     La lettre x trois fois sur quatre, sinon n, t ou y. Correction
 *     positionnelle et exacte (strict), comme 1049.
 *   Carte 2 « Quelle solution ? » (solution_qcm_unique) : « x + 5 = 12 »
 *     QUATRE choix 7 (clé), 17 (operation_directe, LA principale : QCM de
 *     943), −7 (inversion), 12 (x_ignore) : −7 < 7 < 12 < 17, la clé
 *     encadrée ; « 3x = 12 » : 4, 36, 9 (lecture_additive), 1/4 (inversion) :
 *     1/4 < 4 < 9 < 36.
 *   Carte 3 « Quel calcul donne x ? » (calcul_qcm_unique, nom de 1095/1046) :
 *     QCM d'écritures « 12 − 5 » (clé), « 12 + 5 », « 5 − 12 », « 12 ÷ 5 » ;
 *     « 12 ÷ 3 », « 12 × 3 », « 3 ÷ 12 », « 12 − 3 » : pur rappel du geste
 *     (doctrine, règle 9), sans fiche propre.
 *
 * INVARIANTS : x + b = c avec b, c entiers 1..50 et c > b (solution négative
 * = 1091 : exclue, À ARBITRER) ; décimal à une décimale une ligne sur
 * quatre ; ax = c avec a de 2 à 9, c multiple de a (solution entière) ou
 * c de 1 à 20 non multiple (fraction irréductible) ; 2x = 0 exclu (D7 de
 * 943) ; tout en dixièmes entiers, chaque solution re-vérifiée (throw).
 * SACRIFIÉS (fiche d'aide à venir, deux pages : « Ce qui manque » x + 14 =
 * 32 et « 4 parts égales qui font 13 » 4x = 13) : (14 ; 32) posé ici (1046
 * n'exporte pas de sacrifice) et (4 ; 13) importé de 1049, sous toutes
 * leurs formes et lettres, auto-testés.
 *
 * ── Erreurs (familles nommées) ───────────────────────────────────────────
 *   operation_directe : x + 5 = 12 → 17 ; 3x = 12 → 36 (PRINCIPALE, 943) ;
 *   inversion         : 5 − 12 = −7 ; 3x = 12 → 1/4 (nom de 1049) ;
 *   lecture_additive  : 3x lu 3 + x, 3x = 12 → 9 ;
 *   x_ignore          : le nombre de droite recopié (12) ;
 *   soustraction      : 4x = 13 → 9 (nom de 1049).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeTexteTrou, tex,
} from '../../../utils';
import { COUPLES_SACRIFIES as SACRIFIES_1049 } from '../../5eme/nombres-rationnels/gen_1049';

const ATTENDU_ID = 1107;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Quelle solution ?';
const L_CALCUL = 'Quel calcul donne x ?';

const LETTRES = ['x', 'x', 'x', 'n', 't', 'y'];

// ─── Le domaine (dixièmes entiers) ────────────────────────────────────────

/** Les exemples des deux pages de la fiche : x + 14 = 32 (posé ici) et 4x = 13 (importé de 1049). */
export const SACRIFIES = {
    somme: { b: 14, c: 32 },
    produits: SACRIFIES_1049.map(({ a, b }) => ({ a, c: b })),
};
const gcd = (u, v) => (v === 0 ? u : gcd(v, u % v));

/** dixièmes → écriture française (« 4,5 », « 18 »), en KaTeX avec {,}. */
export const fr = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);
export const frTex = (d) => fr(d).replace(',', '{,}');

/**
 * Un item : { type: 'somme', b, c, x } (dixièmes, x = c − b) ou
 * { type: 'produit', a, c, x: { num, den } } (x = c/a réduit). Assert.
 */
export const somme = (b, c) => {
    if (!Number.isInteger(b) || !Number.isInteger(c) || b < 10 || c <= b || c > 500) throw new Error(`gen_1107 : somme hors domaine ${b} ${c}`);
    if (b === SACRIFIES.somme.b * 10 && c === SACRIFIES.somme.c * 10) throw new Error('gen_1107 : somme sacrifiée');
    const x = c - b;
    if (b + x !== c) throw new Error('gen_1107 : arithmétique');
    return { type: 'somme', b, c, x };
};
export const produit = (a, c) => {
    if (!Number.isInteger(a) || !Number.isInteger(c) || a < 2 || a > 9 || c < 1 || c > 90) throw new Error(`gen_1107 : produit hors domaine ${a} ${c}`);
    if (SACRIFIES.produits.some((s) => s.a === a && s.c === c)) throw new Error('gen_1107 : produit sacrifié');
    // solution entière (c multiple de a) ou fraction c/a IRRÉDUCTIBLE, écrite avec les nombres donnés (simplifier = objectif l. 660)
    if (c % a !== 0 && gcd(c, a) !== 1) throw new Error(`gen_1107 : fraction réductible ${c}/${a}`);
    const x = c % a === 0 ? { num: c / a, den: 1 } : { num: c, den: a };
    if (a * x.num !== c * x.den) throw new Error('gen_1107 : arithmétique');
    return { type: 'produit', a, c, x };
};

export const tirerSomme = ({ decimal = false, bDevant = false } = {}) => {
    for (let essai = 0; essai < 60; essai++) {
        const b = decimal ? randInt(11, 199) : randInt(1, 40) * 10;
        const x = decimal ? randInt(11, 199) : randInt(1, 40) * 10;
        if (decimal && b % 10 === 0 && x % 10 === 0) continue;
        const c = b + x;
        if (c > 500 || (b === 140 && c === 320)) continue;
        return { ...somme(b, c), bDevant };
    }
    return { ...somme(50, 120), bDevant };
};
export const tirerProduit = ({ entier = true } = {}) => {
    for (let essai = 0; essai < 60; essai++) {
        const a = randInt(2, 9);
        const c = entier ? a * randInt(2, 10) : randInt(1, 20);
        if ((!entier && (c % a === 0 || gcd(c, a) !== 1)) || SACRIFIES.produits.some((s) => s.a === a && s.c === c)) continue;
        return produit(a, c);
    }
    return produit(entier ? 3 : 5, entier ? 12 : 8);
};

// ─── Écritures ────────────────────────────────────────────────────────────

/** « x + 14 = 32 » / « 14 + x = 32 » / « 3x = 12 », en KaTeX, avec la lettre. */
export const equationTex = (it, L) => (it.type === 'somme'
    ? (it.bDevant ? `${frTex(it.b)} + ${L} = ${frTex(it.c)}` : `${L} + ${frTex(it.b)} = ${frTex(it.c)}`)
    : `${it.a}${L} = ${it.c}`);
export const fracDeuxCases = (x) => `\\dfrac{{{${x.num}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${x.den}}}}`;
export const fracTex = (n, d) => `\\dfrac{${n}}{${d}}`;

const EXPLICATIONS = {
    operation_directe: 'x + 5 = 12 : x est ce qui manque pour aller de 5 à 12, on retire 5, on n\'ajoute pas. 3x = 12 : on divise 12 par 3.',
    inversion: 'C\'est 12 divisé par 3, pas 3 divisé par 12 ; 12 moins 5, pas 5 moins 12.',
    lecture_additive: '3x, c\'est 3 × x, pas 3 + x : la solution de 3x = 12 est 4.',
    x_ignore: 'Le nombre de droite n\'est pas la solution : il faut défaire l\'opération.',
    soustraction: '4x = 13 : x n\'est pas 13 − 4, c\'est 13 ÷ 4, la fraction 13/4.',
    regle: 'x + b = c : x = c − b. ax = c : x = c ÷ a, écrit en fraction si ça ne tombe pas juste.',
};

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ─────────────

/** Le code commun de chaque famille (catalogue validé) ; x_ignore est ici le nombre de droite recopié ; signes_confondus est rejeté ; résoudre 3y = 8 par une soustraction a son propre code depuis le lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md). */
const CODE_COMMUN = {
    operation_directe: 'operation_directe', inversion: 'inversion', x_ignore: 'membre_droit_recopie',
    lecture_additive: 'equation_produit_par_soustraction', soustraction: 'equation_produit_par_soustraction', signes_confondus: null,
};

/** Clé de comparaison d'une réponse : sa valeur si c'est un nombre (4,6 = 4.60), son texte sinon. */
const cleReponse = (v) => {
    const s = String(v).replace(/\s/g, '').replace(',', '.');
    return s !== '' && Number.isFinite(Number(s)) ? `n${Number(s)}` : `t${s.toLowerCase()}`;
};

/** Les erreurs nommables parmi des candidats { code, valeur } (code null : famille rejetée) : jamais la bonne réponse, jamais une valeur que deux codes donnent. */
const nommer = (candidats, bonne) => {
    const groupes = new Map();
    candidats.forEach((c) => {
        const k = cleReponse(c.valeur);
        if (k !== cleReponse(bonne)) groupes.set(k, [...(groupes.get(k) || []), c]);
    });
    return [...groupes.values()]
        .filter((g) => g[0].code && g.every((c) => c.code === g[0].code))
        .map((g) => ({ code: g[0].code, valeur: String(g[0].valeur) }));
};

/** Range les erreurs nommées dans la question (rien si aucune). */
const ranger = (payload, erreurs) => {
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};

/** Les leurres d'un QCM ({ texte du choix, code du gen }) sous leur code commun. */
const erreursQcm = (leurres, bon) => nommer(leurres.map((l) => ({ code: CODE_COMMUN[l.code], valeur: l.texte })), bon);

/** Série flash, trou par trou (une fraction en a deux) : c, c + b, b − c ; c, a × c, c − a (les leurres du QCM) ; c/a écrite a/c. */
const erreursFlash = (items) => {
    const places = items.flatMap((it) => {
        if (it.type === 'somme') {
            return [nommer([
                { code: CODE_COMMUN.x_ignore, valeur: fr(it.c) }, { code: CODE_COMMUN.operation_directe, valeur: fr(it.c + it.b) },
                { code: CODE_COMMUN.inversion, valeur: `-${fr(it.x)}` },
            ], fr(it.x))];
        }
        if (it.x.den === 1) {
            return [nommer([
                { code: CODE_COMMUN.x_ignore, valeur: String(it.c) }, { code: CODE_COMMUN.operation_directe, valeur: String(it.a * it.c) },
                ...(it.c - it.a >= 1 ? [{ code: CODE_COMMUN.lecture_additive, valeur: String(it.c - it.a) }] : []),
            ], String(it.x.num))];
        }
        return [nommer([{ code: CODE_COMMUN.inversion, valeur: String(it.a) }], String(it.x.num)), nommer([{ code: CODE_COMMUN.inversion, valeur: String(it.c) }], String(it.x.den))];
    });
    return places.flatMap((nommees, trou) => nommees.map(({ code, valeur }) => ({ code, trou, valeur })));
};

// ─── Carte 1 : Série flash ────────────────────────────────────────────────

const CONSIGNE_FLASH = `<p><b>Résous chaque équation.</b> Rappel : ${tex('\\dfrac{a}{b}')} est le nombre qui, multiplié par ${tex('b')}, donne ${tex('a')}.</p>`;

const ligneFlash = (it, L) => {
    const sol = it.type === 'produit' && it.x.den !== 1 ? fracDeuxCases(it.x) : `{{${it.type === 'somme' ? fr(it.x) : it.x.num}}}`;
    return `$${equationTex(it, L)} \\qquad ${L} = ${sol}$`;
};

const assemblerFlash = (items, L) => {
    const payload = makeTexteTrou(items.map((it) => ligneFlash(it, L)).join('\n'), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: true });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    ranger(payload, erreursFlash(items));
    payload._meta.params = { items, lettre: L };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        const L = randomChoice(LETTRES);
        const items = [
            tirerSomme(),
            tirerProduit({ entier: true }),
            tirerProduit({ entier: false }),
            randomChoice([true, false]) ? tirerSomme({ decimal: true, bDevant: randomChoice([true, false]) }) : tirerSomme({ bDevant: true }),
        ];
        const cles = items.map((it) => (it.type === 'somme' ? `s${it.b}/${it.c}` : `p${it.a}/${it.c}`));
        if (new Set(cles).size < 4) continue;
        return assemblerFlash(shuffle(items), L);
    }
    return assemblerFlash([tirerSomme(), produit(3, 12), produit(5, 8), tirerSomme({ bDevant: true })], 'x');
};

// ─── Carte 2 : QCM « Quelle solution ? » ──────────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const L = randomChoice(LETTRES);
        const it = randomChoice([true, false]) ? tirerSomme({ bDevant: randomChoice([true, false]) }) : tirerProduit({ entier: true });
        let bon;
        let faux;
        if (it.type === 'somme') {
            const [b, c, x] = [it.b / 10, it.c / 10, it.x / 10];
            bon = String(x);
            faux = [
                { texte: String(c + b), code: 'operation_directe' },
                { texte: String(b - c), code: 'inversion' },
                { texte: String(c), code: 'x_ignore' },
            ];
        } else {
            const { a, c, x } = it;
            bon = String(x.num);
            faux = [
                { texte: String(a * c), code: 'operation_directe' },
                { texte: String(c - a), code: 'lecture_additive' },
                { texte: `$${fracTex(a, c)}$`, code: 'inversion' },
            ];
            if (c - a === x.num || c - a < 1) continue; // 2x = 4 : 4 − 2 est la clé
        }
        if (new Set([bon, ...faux.map((f) => f.texte)]).size !== 4) continue;
        const payload = makeQcm([bon, ...faux.map((f) => f.texte)], bon, `<p><b>Quelle est la solution de ${tex(equationTex(it, L))} ?</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        ranger(payload, erreursQcm(faux, bon));
        payload._meta.params = { ...it, lettre: L, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const it = { ...somme(50, 120), bDevant: false };
    const payload = makeQcm(['7', '17', '-7', '12'], '7', `<p><b>Quelle est la solution de ${tex('x + 5 = 12')} ?</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, erreursQcm([{ texte: '17', code: 'operation_directe' }, { texte: '-7', code: 'inversion' }, { texte: '12', code: 'x_ignore' }], '7'));
    payload._meta.params = { ...it, lettre: 'x', distracteurs: [{ code: 'operation_directe' }, { code: 'inversion' }, { code: 'x_ignore' }] };
    return payload;
};

// ─── Carte 3 : QCM d'écriture « Quel calcul donne x ? » ───────────────────

const genCalcul = () => {
    for (let essai = 0; essai < 80; essai++) {
        const L = randomChoice(LETTRES);
        const it = randomChoice([true, false]) ? tirerSomme({ bDevant: randomChoice([true, false]) }) : tirerProduit({ entier: randomChoice([true, false]) });
        let choix;
        if (it.type === 'somme') {
            const [b, c] = [it.b / 10, it.c / 10];
            choix = [[`$${c} - ${b}$`, null], [`$${c} + ${b}$`, 'operation_directe'], [`$${b} - ${c}$`, 'inversion'], [`$${c} \\div ${b}$`, 'signes_confondus']];
        } else {
            const { a, c } = it;
            choix = [[`$${c} \\div ${a}$`, null], [`$${c} \\times ${a}$`, 'operation_directe'], [`$${a} \\div ${c}$`, 'inversion'], [`$${c} - ${a}$`, 'soustraction']];
        }
        if (new Set(choix.map(([t]) => t)).size !== 4) continue;
        const bon = choix[0][0];
        const payload = makeQcm(choix.map(([t]) => t), bon, `<p><b>Quel calcul donne ${tex(L)} dans ${tex(equationTex(it, L))} ?</b></p>`, opts(L_CALCUL));
        payload.contenu = { ...payload.contenu, explications: { ...EXPLICATIONS, signes_confondus: 'Pour défaire une addition, on soustrait.' } };
        ranger(payload, erreursQcm(choix.slice(1).map(([texte, code]) => ({ texte, code })), bon));
        payload._meta.params = { ...it, lettre: L, distracteurs: choix.slice(1).map(([, code]) => ({ code })) };
        return payload;
    }
    const it = produit(3, 12);
    const payload = makeQcm(['$12 \\div 3$', '$12 \\times 3$', '$3 \\div 12$', '$12 - 3$'], '$12 \\div 3$', `<p><b>Quel calcul donne ${tex('x')} dans ${tex('3x = 12')} ?</b></p>`, opts(L_CALCUL));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, erreursQcm([{ texte: '$12 \\times 3$', code: 'operation_directe' }, { texte: '$3 \\div 12$', code: 'inversion' }, { texte: '$12 - 3$', code: 'soustraction' }], '$12 \\div 3$'));
    payload._meta.params = { ...it, lettre: 'x', distracteurs: [{ code: 'operation_directe' }, { code: 'inversion' }, { code: 'soustraction' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre choix distincts ; sommes : −7 < 7 < 12 < 17 (clé encadrée) ; produits : 1/4 < 4 < 9 < 36',
        'sacrifiés : (14 ; 32) et (4 ; 13) importé de 1049, sous toutes les lettres',
    ],
    solution_qcm_unique: {
        support: 'x + b = c (b, c entiers 1..40, c > b) ou ax = c à solution entière (a 2..9)',
        familles: {
            operation_directe: 'c + b ; a × c : l\'opération de l\'équation refaite au lieu d\'être défaite (QCM de 943, la principale)',
            inversion: 'b − c (négatif) ; a/c (nom de 1049)',
            lecture_additive: '3x lu 3 + x : 3x = 12 → 9',
            x_ignore: 'le nombre de droite recopié',
        },
    },
    calcul_qcm_unique: 'quatre écritures : c − b / c + b / b − c / c ÷ b ; c ÷ a / c × a / a ÷ c / c − a (soustraction, nom de 1049) ; pur rappel du geste, sans fiche',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    solution_qcm_unique: genQcm,
    calcul_qcm_unique: genCalcul,
};

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) ; sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, etapes: 1, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'solution', label: L_QCM, etapes: 1, formatsList: [{ key: 'solution_qcm_unique', label: 'QCM' }] },
    { id: 'calcul', label: L_CALCUL, etapes: 1, formatsList: [{ key: 'calcul_qcm_unique', label: 'QCM' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
